import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, BellOff, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { getNotificationPermissionState, prepareNotificationRuntime, requestNotificationRegistration } from '@/lib/notifications';
import { apiRequest } from '@/lib/queryClient';

interface NotificationPermissionProps {
  playerId: string;
  onPermissionGranted?: () => void;
}

const NOTIFICATION_DISMISSED_KEY = 'notification_permission_dismissed';

export function NotificationPermission({ playerId, onPermissionGranted }: NotificationPermissionProps) {
  const { t } = useTranslation();
  const [isRequesting, setIsRequesting] = useState(false);
  const [permission, setPermission] = useState<'default' | 'granted' | 'denied' | null>(null);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    const loadPermission = async () => {
      const state = await getNotificationPermissionState();
      setPermission(state);

      // Check if user previously dismissed with "Never"
      const dismissed = localStorage.getItem(NOTIFICATION_DISMISSED_KEY);
      if (dismissed === 'true') {
        setIsDismissed(true);
      }
    };

    loadPermission();
  }, []);

  const handleRequestPermission = async () => {
    setIsRequesting(true);

    try {
      await prepareNotificationRuntime();

      const registration = await requestNotificationRegistration();

      if (registration) {
        await apiRequest('POST', '/api/notifications/register-device-token', {
          token: registration.token,
          platform: registration.platform
        });

        setPermission('granted');
        // Clear dismissed state if permission is granted
        localStorage.removeItem(NOTIFICATION_DISMISSED_KEY);
        onPermissionGranted?.();
        console.log('✅ Device token registered successfully:', registration.platform);
      } else {
        console.warn('⚠️ Failed to get notification token. This may be normal if running in a development environment or if permissions were denied.');
      }
    } catch (error) {
      console.error('❌ Error requesting notification permission:', error);
    } finally {
      setIsRequesting(false);
    }
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    // Persist the dismissal choice
    localStorage.setItem(NOTIFICATION_DISMISSED_KEY, 'true');
  };

  // Don't show if permission is granted, denied, or dismissed
  if (permission === 'granted' || permission === 'denied' || isDismissed) {
    return null;
  }

  return (
    <div id="notification-permission-modal" className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md mx-auto bg-card shadow-2xl">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell className="h-6 w-6 text-blue-600" />
              <CardTitle className="text-xl text-foreground">
                {t('notifications.permission.title')}
              </CardTitle>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDismiss}
              className="h-6 w-6 p-0 text-muted-foreground hover:text-muted-foreground"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <CardDescription className="text-muted-foreground mt-2">
            {t('notifications.permission.description')}
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex flex-col gap-3">
            <Button
              onClick={handleRequestPermission}
              disabled={isRequesting}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isRequesting ? (
                <>
                  <Bell className="h-4 w-4 mr-2 animate-pulse" />
                  {t('notifications.permission.activating')}
                </>
              ) : (
                <>
                  <Bell className="h-4 w-4 mr-2" />
                  {t('notifications.permission.action')}
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={handleDismiss}
              className="w-full text-muted-foreground border-border hover:bg-muted"
            >
              <BellOff className="h-4 w-4 mr-2" />
              {t('notifications.permission.never')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
